// Feature codes from backend FeatureType enum
// ⚠️ CRITICAL: Must match backend exactly!
export const FEATURES = {
  // ─── Common Features (00XX) ───────────────────────────────────────────────
  COMMON_DASHBOARD: 1,
  COMMON_REPORTS: 2,
  COMMON_POS: 3,
  COMMON_MULTI_LOCATION: 4,
  COMMON_WHITE_LABEL: 5,

  // ─── Staff & HR Features (04XX) ───────────────────────────────────────────
  COMMON_STAFF_MANAGEMENT: 401,
  COMMON_HR_MANAGEMENT: 402,
  COMMON_SHIFT_SCHEDULING: 403,
  COMMON_TIME_ATTENDANCE: 404,

  // ─── Analytics Features (05XX) ────────────────────────────────────────────
  COMMON_ANALYTICS: 501,
  COMMON_ADVANCED_REPORTS: 502,
  COMMON_CUSTOM_REPORTS: 503,
  COMMON_REALTIME_DASHBOARD: 504,

  // ─── Marketing Features (06XX) ────────────────────────────────────────────
  COMMON_LOYALTY_PROGRAM: 601,
  COMMON_ADVANCED_LOYALTY: 602,
  COMMON_MARKETING: 603,
  COMMON_PROMOTIONS: 604,

  // ─── Integration Features (08XX) ──────────────────────────────────────────
  COMMON_API_ACCESS: 801,
  COMMON_WEBHOOKS: 802,
  COMMON_INTEGRATIONS: 803,

  // ─── Enterprise Features (09XX) ───────────────────────────────────────────
  COMMON_BUSINESS_AUDIT: 901,
  COMMON_SECURITY_ALERTS: 902,
  COMMON_COMPLIANCE_REPORTS: 903,
  COMMON_DATA_EXPORT: 904,
  COMMON_SSO: 905,

  // ─── Core Restaurant (10XX) ───────────────────────────────────────────────
  RESTO_MANAGE_RESTAURANT: 1001,
  RESTO_BASIC_MENU: 1002,
  RESTO_GENERATE_QR: 1003,
  RESTO_DIGITAL_MENU: 1004,

  // ─── Operations (11XX) ────────────────────────────────────────────────────
  RESTO_MANAGE_TABLES: 1101,
  RESTO_TABLE_RESERVATION: 1102,
  RESTO_ORDER_MANAGEMENT: 1103,
  RESTO_KITCHEN_DISPLAY: 1104,
  RESTO_WAITER_MANAGEMENT: 1105,
  RESTO_SPLIT_BILLING: 1106,

  // ─── Inventory (12XX) ─────────────────────────────────────────────────────
  RESTO_INVENTORY: 1201,
  RESTO_RECIPE_LIBRARY: 1202,
  RESTO_COST_CALCULATOR: 1203,
  RESTO_SUPPLIER_MANAGEMENT: 1204,
  RESTO_WASTAGE_TRACKING: 1205,
  RESTO_STOCK_ALERTS: 1206,

  // ─── Customer Features (13XX) ─────────────────────────────────────────────
  RESTO_ONLINE_ORDERING: 1301,
  RESTO_DELIVERY_MANAGEMENT: 1302,
  RESTO_CUSTOMER_FEEDBACK: 1303,
  RESTO_WAITLIST: 1304,

  // ─── AI/Smart Features (15XX) ─────────────────────────────────────────────
  RESTO_INTELLIGENT_MENU: 1501,
  RESTO_AI_SUGGESTIONS: 1502,
  RESTO_SMART_PRICING: 1503,
  RESTO_MENU_ANALYTICS: 1504,
  RESTO_PREDICTIVE_ORDERING: 1505,

  // ─── Legacy mappings (for backward compatibility) ─────────────────────────
  // These map old feature codes to new ones
  DASHBOARD: 1,
  REPORTS: 2,
  POS: 3,
  MENU_MANAGEMENT: 1002, // Now RESTO_BASIC_MENU
  INTELLIGENT_MENU: 1501, // Now RESTO_INTELLIGENT_MENU
  COST_CALCULATOR: 1203, // Now RESTO_COST_CALCULATOR
  INVENTORY: 1201, // Now RESTO_INVENTORY
  RECIPE_LIBRARY: 1202, // Now RESTO_RECIPE_LIBRARY
  AI_SUGGESTIONS: 1502, // Now RESTO_AI_SUGGESTIONS
  SMART_PRICING: 1503, // Now RESTO_SMART_PRICING
  ANALYTICS: 501, // Now COMMON_ANALYTICS
  ORDER_PROCESSING: 1103, // Now RESTO_ORDER_MANAGEMENT
  STAFF_MANAGEMENT: 401, // Now COMMON_STAFF_MANAGEMENT
  HR_MANAGEMENT: 402, // Now COMMON_HR_MANAGEMENT
  MANAGE_TABLES: 1101, // Now RESTO_MANAGE_TABLES
  GENERATE_QR: 1003, // Now RESTO_GENERATE_QR
  MANAGE_RESTAURANT: 1001, // Now RESTO_MANAGE_RESTAURANT
  ORDER_MANAGEMENT: 1103, // Now RESTO_ORDER_MANAGEMENT
  LOYALTY_PROGRAM: 601, // Now COMMON_LOYALTY_PROGRAM
} as const;

// Feature names for display
export const FEATURE_NAMES: Record<number, string> = {
  // Common Features
  [FEATURES.COMMON_DASHBOARD]: "Dashboard",
  [FEATURES.COMMON_REPORTS]: "Reports",
  [FEATURES.COMMON_POS]: "POS System",
  [FEATURES.COMMON_MULTI_LOCATION]: "Multi-Location Support",
  [FEATURES.COMMON_WHITE_LABEL]: "White-Label Branding",

  // Staff & HR
  [FEATURES.COMMON_STAFF_MANAGEMENT]: "Staff Management",
  [FEATURES.COMMON_HR_MANAGEMENT]: "HR Management",
  [FEATURES.COMMON_SHIFT_SCHEDULING]: "Shift Scheduling",
  [FEATURES.COMMON_TIME_ATTENDANCE]: "Time & Attendance",

  // Analytics
  [FEATURES.COMMON_ANALYTICS]: "Analytics",
  [FEATURES.COMMON_ADVANCED_REPORTS]: "Advanced Reports",
  [FEATURES.COMMON_CUSTOM_REPORTS]: "Custom Reports",
  [FEATURES.COMMON_REALTIME_DASHBOARD]: "Real-Time Dashboard",

  // Marketing
  [FEATURES.COMMON_LOYALTY_PROGRAM]: "Loyalty Program",
  [FEATURES.COMMON_ADVANCED_LOYALTY]: "Advanced Loyalty",
  [FEATURES.COMMON_MARKETING]: "Marketing",
  [FEATURES.COMMON_PROMOTIONS]: "Promotions",

  // Integration
  [FEATURES.COMMON_API_ACCESS]: "API Access",
  [FEATURES.COMMON_WEBHOOKS]: "Webhooks",
  [FEATURES.COMMON_INTEGRATIONS]: "Third-Party Integrations",

  // Enterprise
  [FEATURES.COMMON_BUSINESS_AUDIT]: "Business Audit",
  [FEATURES.COMMON_SECURITY_ALERTS]: "Security Alerts",
  [FEATURES.COMMON_COMPLIANCE_REPORTS]: "Compliance Reports",
  [FEATURES.COMMON_DATA_EXPORT]: "Data Export",
  [FEATURES.COMMON_SSO]: "SSO Authentication",

  // Core Restaurant
  [FEATURES.RESTO_MANAGE_RESTAURANT]: "Manage Restaurant",
  [FEATURES.RESTO_BASIC_MENU]: "Menu Management",
  [FEATURES.RESTO_GENERATE_QR]: "Generate QR Code",
  [FEATURES.RESTO_DIGITAL_MENU]: "Digital Menu",

  // Operations
  [FEATURES.RESTO_MANAGE_TABLES]: "Manage Tables",
  [FEATURES.RESTO_TABLE_RESERVATION]: "Table Reservation",
  [FEATURES.RESTO_ORDER_MANAGEMENT]: "Order Management",
  [FEATURES.RESTO_KITCHEN_DISPLAY]: "Kitchen Display System",
  [FEATURES.RESTO_WAITER_MANAGEMENT]: "Waiter Management",
  [FEATURES.RESTO_SPLIT_BILLING]: "Split Billing",

  // Inventory
  [FEATURES.RESTO_INVENTORY]: "Inventory Management",
  [FEATURES.RESTO_RECIPE_LIBRARY]: "Recipe Library",
  [FEATURES.RESTO_COST_CALCULATOR]: "Cost Calculator",
  [FEATURES.RESTO_SUPPLIER_MANAGEMENT]: "Supplier Management",
  [FEATURES.RESTO_WASTAGE_TRACKING]: "Wastage Tracking",
  [FEATURES.RESTO_STOCK_ALERTS]: "Stock Alerts",

  // Customer Features
  [FEATURES.RESTO_ONLINE_ORDERING]: "Online Ordering",
  [FEATURES.RESTO_DELIVERY_MANAGEMENT]: "Delivery Management",
  [FEATURES.RESTO_CUSTOMER_FEEDBACK]: "Customer Feedback",
  [FEATURES.RESTO_WAITLIST]: "Waitlist Management",

  // AI/Smart Features
  [FEATURES.RESTO_INTELLIGENT_MENU]: "Intelligent Menu",
  [FEATURES.RESTO_AI_SUGGESTIONS]: "AI Suggestions",
  [FEATURES.RESTO_SMART_PRICING]: "Smart Pricing",
  [FEATURES.RESTO_MENU_ANALYTICS]: "Menu Analytics",
  [FEATURES.RESTO_PREDICTIVE_ORDERING]: "Predictive Ordering",
};

// Plan feature matrix - MUST MATCH BACKEND EXACTLY!
export const PLAN_FEATURES = {
  // Basic Plan: Core features only
  BASIC: [
    FEATURES.COMMON_DASHBOARD,
    FEATURES.COMMON_POS,
    FEATURES.RESTO_MANAGE_RESTAURANT,
    FEATURES.RESTO_BASIC_MENU,
    FEATURES.RESTO_ORDER_MANAGEMENT,
  ],
  // Pro Plan: Advanced features
  PRO: [
    FEATURES.COMMON_DASHBOARD,
    FEATURES.COMMON_REPORTS,
    FEATURES.COMMON_POS,
    FEATURES.COMMON_STAFF_MANAGEMENT,
    FEATURES.COMMON_ANALYTICS,
    FEATURES.COMMON_LOYALTY_PROGRAM,
    FEATURES.RESTO_MANAGE_RESTAURANT,
    FEATURES.RESTO_BASIC_MENU,
    FEATURES.RESTO_GENERATE_QR,
    FEATURES.RESTO_DIGITAL_MENU,
    FEATURES.RESTO_MANAGE_TABLES,
    FEATURES.RESTO_TABLE_RESERVATION,
    FEATURES.RESTO_ORDER_MANAGEMENT,
    FEATURES.RESTO_INVENTORY,
    FEATURES.RESTO_RECIPE_LIBRARY,
    FEATURES.RESTO_COST_CALCULATOR,
    FEATURES.RESTO_INTELLIGENT_MENU,
    FEATURES.RESTO_AI_SUGGESTIONS,
    FEATURES.RESTO_SMART_PRICING,
    FEATURES.RESTO_MENU_ANALYTICS,
  ],
  // Enterprise Plan: All features
  ENTERPRISE: [
    FEATURES.COMMON_DASHBOARD,
    FEATURES.COMMON_REPORTS,
    FEATURES.COMMON_POS,
    FEATURES.COMMON_MULTI_LOCATION,
    FEATURES.COMMON_WHITE_LABEL,
    FEATURES.COMMON_STAFF_MANAGEMENT,
    FEATURES.COMMON_HR_MANAGEMENT,
    FEATURES.COMMON_SHIFT_SCHEDULING,
    FEATURES.COMMON_TIME_ATTENDANCE,
    FEATURES.COMMON_ANALYTICS,
    FEATURES.COMMON_ADVANCED_REPORTS,
    FEATURES.COMMON_CUSTOM_REPORTS,
    FEATURES.COMMON_REALTIME_DASHBOARD,
    FEATURES.COMMON_LOYALTY_PROGRAM,
    FEATURES.COMMON_ADVANCED_LOYALTY,
    FEATURES.COMMON_MARKETING,
    FEATURES.COMMON_PROMOTIONS,
    FEATURES.COMMON_API_ACCESS,
    FEATURES.COMMON_WEBHOOKS,
    FEATURES.COMMON_INTEGRATIONS,
    FEATURES.COMMON_BUSINESS_AUDIT,
    FEATURES.COMMON_SECURITY_ALERTS,
    FEATURES.COMMON_COMPLIANCE_REPORTS,
    FEATURES.COMMON_DATA_EXPORT,
    FEATURES.COMMON_SSO,
    FEATURES.RESTO_MANAGE_RESTAURANT,
    FEATURES.RESTO_BASIC_MENU,
    FEATURES.RESTO_GENERATE_QR,
    FEATURES.RESTO_DIGITAL_MENU,
    FEATURES.RESTO_MANAGE_TABLES,
    FEATURES.RESTO_TABLE_RESERVATION,
    FEATURES.RESTO_ORDER_MANAGEMENT,
    FEATURES.RESTO_KITCHEN_DISPLAY,
    FEATURES.RESTO_WAITER_MANAGEMENT,
    FEATURES.RESTO_SPLIT_BILLING,
    FEATURES.RESTO_INVENTORY,
    FEATURES.RESTO_RECIPE_LIBRARY,
    FEATURES.RESTO_COST_CALCULATOR,
    FEATURES.RESTO_SUPPLIER_MANAGEMENT,
    FEATURES.RESTO_WASTAGE_TRACKING,
    FEATURES.RESTO_STOCK_ALERTS,
    FEATURES.RESTO_ONLINE_ORDERING,
    FEATURES.RESTO_DELIVERY_MANAGEMENT,
    FEATURES.RESTO_CUSTOMER_FEEDBACK,
    FEATURES.RESTO_WAITLIST,
    FEATURES.RESTO_INTELLIGENT_MENU,
    FEATURES.RESTO_AI_SUGGESTIONS,
    FEATURES.RESTO_SMART_PRICING,
    FEATURES.RESTO_MENU_ANALYTICS,
    FEATURES.RESTO_PREDICTIVE_ORDERING,
  ],
} as const;

// Helper to get plan name by feature set
export const getPlanByFeatures = (features: number[]): string => {
  const featureCount = features.length;

  if (featureCount >= PLAN_FEATURES.ENTERPRISE.length) {
    return "Enterprise";
  } else if (featureCount >= PLAN_FEATURES.PRO.length) {
    return "Pro";
  }
  return "Basic";
};
